'use client';

import React, { useMemo } from 'react';
import { ShieldCheck, AlertTriangle, XCircle, Clock, Users, TrendingUp } from 'lucide-react';
import type { Credential, StaffCompliance } from '../page';
import Icon from '@/components/ui/AppIcon';


interface ComplianceDashboardProps {
  credentials: Credential[];
}

const DEPT_COLORS: Record<string, string> = {
  ICU: 'bg-blue-500',
  'Med-Surg': 'bg-violet-500',
  Emergency: 'bg-rose-500',
  Pediatrics: 'bg-amber-500',
  Cardiology: 'bg-cyan-500',
  Oncology: 'bg-pink-500',
};

function statusLabel(s: Credential['status']) {
  if (s === 'active') return 'Active';
  if (s === 'expiring_soon') return 'Expiring Soon';
  if (s === 'expired') return 'Expired';
  return 'Pending Renewal';
}

function statusColor(s: Credential['status']) {
  if (s === 'active') return 'text-success';
  if (s === 'expiring_soon') return 'text-warning';
  if (s === 'expired') return 'text-danger';
  return 'text-primary';
}

function statusBg(s: Credential['status']) {
  if (s === 'active') return 'bg-success/10';
  if (s === 'expiring_soon') return 'bg-warning/10';
  if (s === 'expired') return 'bg-danger/10';
  return 'bg-primary/10';
}

export default function ComplianceDashboard({ credentials }: ComplianceDashboardProps) {
  const stats = useMemo(() => {
    const total = credentials.length;
    const active = credentials.filter((c) => c.status === 'active').length;
    const expiringSoon = credentials.filter((c) => c.status === 'expiring_soon').length;
    const expired = credentials.filter((c) => c.status === 'expired').length;
    const pending = credentials.filter((c) => c.status === 'pending_renewal').length;
    const complianceRate = total > 0 ? Math.round(((active + pending) / total) * 100) : 0;
    return { total, active, expiringSoon, expired, pending, complianceRate };
  }, [credentials]);

  // Per-staff compliance
  const staffCompliance = useMemo<StaffCompliance[]>(() => {
    const map = new Map<string, StaffCompliance>();
    credentials.forEach((c) => {
      if (!map.has(c.staffId)) {
        map.set(c.staffId, {
          staffId: c.staffId,
          staffName: c.staffName,
          role: c.role,
          department: c.department,
          totalCredentials: 0,
          activeCount: 0,
          expiringSoonCount: 0,
          expiredCount: 0,
          pendingCount: 0,
          complianceScore: 0,
        });
      }
      const entry = map.get(c.staffId)!;
      entry.totalCredentials++;
      if (c.status === 'active') entry.activeCount++;
      if (c.status === 'expiring_soon') entry.expiringSoonCount++;
      if (c.status === 'expired') entry.expiredCount++;
      if (c.status === 'pending_renewal') entry.pendingCount++;
    });
    map.forEach((entry) => {
      entry.complianceScore = entry.totalCredentials > 0
        ? Math.round(((entry.activeCount + entry.pendingCount) / entry.totalCredentials) * 100)
        : 0;
    });
    return Array.from(map.values()).sort((a, b) => a.complianceScore - b.complianceScore);
  }, [credentials]);

  // By department
  const deptStats = useMemo(() => {
    const map = new Map<string, { total: number; compliant: number }>();
    credentials.forEach((c) => {
      if (!map.has(c.department)) map.set(c.department, { total: 0, compliant: 0 });
      const d = map.get(c.department)!;
      d.total++;
      if (c.status === 'active' || c.status === 'pending_renewal') d.compliant++;
    });
    return Array.from(map.entries()).map(([dept, v]) => ({
      dept,
      total: v.total,
      compliant: v.compliant,
      rate: Math.round((v.compliant / v.total) * 100),
    })).sort((a, b) => a.rate - b.rate);
  }, [credentials]);

  // By category
  const categoryStats = useMemo(() => {
    const cats: Credential['category'][] = ['license', 'certification', 'training', 'background'];
    return cats.map((cat) => {
      const items = credentials.filter((c) => c.category === cat);
      const compliant = items.filter((c) => c.status === 'active' || c.status === 'pending_renewal').length;
      return { cat, total: items.length, compliant, rate: items.length > 0 ? Math.round((compliant / items.length) * 100) : 0 };
    });
  }, [credentials]);

  const kpiCards = [
    { label: 'Total Credentials', value: stats.total, icon: ShieldCheck, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Active & Compliant', value: stats.active + stats.pending, icon: TrendingUp, color: 'text-success', bg: 'bg-success/10' },
    { label: 'Expiring ≤ 30 Days', value: stats.expiringSoon, icon: Clock, color: 'text-warning', bg: 'bg-warning/10' },
    { label: 'Expired', value: stats.expired, icon: XCircle, color: 'text-danger', bg: 'bg-danger/10' },
    { label: 'Pending Renewal', value: stats.pending, icon: AlertTriangle, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Staff Tracked', value: staffCompliance.length, icon: Users, color: 'text-foreground', bg: 'bg-muted' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpiCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-card border border-border rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={card.color} />
              </div>
              <p className="text-2xl font-700 text-foreground">{card.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Overall compliance meter + dept breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Overall compliance */}
        <div className="bg-card border border-border rounded-xl p-5 flex flex-col items-center justify-center">
          <p className="text-sm font-600 text-muted-foreground mb-4">Overall Compliance Rate</p>
          <div className="relative w-36 h-36">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="10" className="text-muted/40" />
              <circle
                cx="60" cy="60" r="50" fill="none"
                stroke={stats.complianceRate >= 80 ? '#22c55e' : stats.complianceRate >= 60 ? '#f59e0b' : '#ef4444'}
                strokeWidth="10"
                strokeDasharray={`${(stats.complianceRate / 100) * 314} 314`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-700 text-foreground">{stats.complianceRate}%</span>
              <span className="text-xs text-muted-foreground">compliant</span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 w-full">
            {[
              { label: 'Active', value: stats.active, color: 'bg-success' },
              { label: 'Pending', value: stats.pending, color: 'bg-primary' },
              { label: 'Expiring', value: stats.expiringSoon, color: 'bg-warning' },
              { label: 'Expired', value: stats.expired, color: 'bg-danger' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${item.color} flex-shrink-0`} />
                <span className="text-xs text-muted-foreground">{item.label}</span>
                <span className="text-xs font-600 text-foreground ml-auto">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Department compliance */}
        <div className="bg-card border border-border rounded-xl p-5 lg:col-span-2">
          <p className="text-sm font-600 text-foreground mb-4">Compliance by Department</p>
          <div className="space-y-3">
            {deptStats.map((d) => (
              <div key={d.dept}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${DEPT_COLORS[d.dept] ?? 'bg-muted-foreground'}`} />
                    <span className="text-sm text-foreground font-500">{d.dept}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{d.compliant}/{d.total}</span>
                    <span className={`text-sm font-700 ${d.rate >= 80 ? 'text-success' : d.rate >= 60 ? 'text-warning' : 'text-danger'}`}>{d.rate}%</span>
                  </div>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${d.rate >= 80 ? 'bg-success' : d.rate >= 60 ? 'bg-warning' : 'bg-danger'}`}
                    style={{ width: `${d.rate}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Category breakdown + Staff compliance table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Category */}
        <div className="bg-card border border-border rounded-xl p-5">
          <p className="text-sm font-600 text-foreground mb-4">By Credential Type</p>
          <div className="space-y-4">
            {categoryStats.map((c) => (
              <div key={c.cat}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-foreground capitalize font-500">{c.cat}</span>
                  <span className={`text-sm font-700 ${c.rate >= 80 ? 'text-success' : c.rate >= 60 ? 'text-warning' : 'text-danger'}`}>{c.rate}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${c.rate >= 80 ? 'bg-success' : c.rate >= 60 ? 'bg-warning' : 'bg-danger'}`}
                    style={{ width: `${c.rate}%` }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{c.compliant} of {c.total} compliant</p>
              </div>
            ))}
          </div>
        </div>

        {/* Staff compliance table */}
        <div className="bg-card border border-border rounded-xl p-5 lg:col-span-2">
          <p className="text-sm font-600 text-foreground mb-4">Staff Compliance Overview</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-600 text-muted-foreground pb-2 pr-4">Staff</th>
                  <th className="text-left text-xs font-600 text-muted-foreground pb-2 pr-4">Dept</th>
                  <th className="text-center text-xs font-600 text-muted-foreground pb-2 pr-4">Total</th>
                  <th className="text-center text-xs font-600 text-muted-foreground pb-2 pr-4">Active</th>
                  <th className="text-center text-xs font-600 text-muted-foreground pb-2 pr-4">Expiring</th>
                  <th className="text-center text-xs font-600 text-muted-foreground pb-2 pr-4">Expired</th>
                  <th className="text-right text-xs font-600 text-muted-foreground pb-2">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {staffCompliance.map((s) => (
                  <tr key={s.staffId} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[11px] font-700 flex items-center justify-center flex-shrink-0">
                          {s.staffName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-500 text-foreground text-xs">{s.staffName}</p>
                          <p className="text-[10px] text-muted-foreground">{s.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">{s.department}</td>
                    <td className="py-2.5 pr-4 text-center text-xs font-500 text-foreground">{s.totalCredentials}</td>
                    <td className="py-2.5 pr-4 text-center text-xs font-500 text-success">{s.activeCount}</td>
                    <td className="py-2.5 pr-4 text-center text-xs font-500 text-warning">{s.expiringSoonCount}</td>
                    <td className="py-2.5 pr-4 text-center text-xs font-500 text-danger">{s.expiredCount}</td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${s.complianceScore >= 80 ? 'bg-success' : s.complianceScore >= 60 ? 'bg-warning' : 'bg-danger'}`}
                            style={{ width: `${s.complianceScore}%` }}
                          />
                        </div>
                        <span className={`text-xs font-700 ${s.complianceScore >= 80 ? 'text-success' : s.complianceScore >= 60 ? 'text-warning' : 'text-danger'}`}>
                          {s.complianceScore}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
